import { useState, useEffect } from 'react'
import { getCachedImageUrl } from '@renderer/utils/imageCacheUtils'

/**
 * React hook that automatically caches and returns a blob URL for an image
 * @param imageUrl - The image URL to cache
 * @param options - Optional configuration
 * @param options.expirationHours - Override default expiration (24 hours)
 * @returns The cached blob URL or original URL if caching fails
 */
export const useCachedImage = (
  imageUrl: string | undefined | null,
  options?: { expirationHours?: number }
): string => {
  const [cachedUrl, setCachedUrl] = useState<string>(imageUrl || '')

  useEffect(() => {
    if (!imageUrl) {
      setCachedUrl('')
      return
    }

    // If URL shouldn't be cached (data URL, blob URL, etc.), use as-is
    // Note: We DO cache paths starting with '/' (public folder assets) and HTTP/HTTPS URLs
    if (
      imageUrl.startsWith('data:') ||
      imageUrl.startsWith('blob:') ||
      (!imageUrl.startsWith('http://') &&
        !imageUrl.startsWith('https://') &&
        !imageUrl.startsWith('/'))
    ) {
      setCachedUrl(imageUrl)
      return
    }

    // Fetch and cache the image (already async)
    getCachedImageUrl(imageUrl, options)
      .then((url) => {
        setCachedUrl(url)
      })
      .catch((error) => {
        console.error('Error caching image:', imageUrl, error)
        // Fallback to original URL on error
        setCachedUrl(imageUrl)
      })
  }, [imageUrl, options?.expirationHours])

  return cachedUrl
}
